import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useFieldArray, useForm, useFormContext } from 'react-hook-form';
import {
  Button,
  Card,
  CardContent,
  CircularProgress,
  Divider,
  Grid,
  Stack,
  Typography,
} from '@mui/material';

import { axios, translates } from '@/utils';
import { Iconic } from '@/commons/iconic';
import { RHFCardSelect, RHFTextField } from '@/commons/hook-form';
import RHFQuantity from '@/commons/hook-form/RHFQuantity';
import { yupResolver } from '@hookform/resolvers/yup';
import { getProductsService } from '@/services';
import { orderProductGridSchema } from './validation';
import { mapperProductsBuilderCustom } from '../../utils/mapper';

export type AutocompleteLabels = Record<string, any>;
type Props = {
  isEdit?: boolean;
  isForm?: boolean;
  defaultValuesItems?: any;
  id?: string;
};

export const ProductItemsGridCustomOrder = ({ isEdit, defaultValuesItems, isForm, id }: Props) => {
  const [interItem, setInterItem] = useState<any>([]);
  const { isLoading, data } = useQuery({
    queryKey: ['products'],
    queryFn: getProductsService,
    select: mapperProductsBuilderCustom,
  });
  const methods = useForm({
    resolver: yupResolver(orderProductGridSchema),
    defaultValues: {
      category: null,
      description: '',
      line: null,
      product: {
        label: '',
        value: '',
      },
      quantity: 1,
    },
  });
  const { control, watch } = useFormContext();
  const { append } = useFieldArray({
    control,
    name: 'items',
  });
  const values = watch();
  const handleAdd = () => {
    const products = interItem ?? [];
    const { getValues, resetField } = methods;
    const valuesTemp = getValues();
    products.push({
      product: valuesTemp.product,
      description: valuesTemp.description,
      quantity: valuesTemp.quantity,
    });
    setInterItem(products);
    resetField('product');
    resetField('description');
    resetField('quantity');
  };
  const completeProduct = () => {
    const { getValues, reset } = methods;
    const valuesTemp = getValues();
    const newDescription = interItem
      .map(
        (item: { product: { label: any }; quantity: any }) =>
          `${item.product.label} X ${item.quantity} `
      )
      .join(', ');
    const generateProduct = {
      product: valuesTemp.category,
      description: newDescription,
      quantity: valuesTemp.quantity,
    };
    append(generateProduct);
    reset();
    setInterItem([]);
  };
  if (isLoading) {
    return <CircularProgress size={48} color="info" />;
  }
  const categoryId: any = methods.watch('category');
  const types = data?.types as any;
  const itemArrayProducts = categoryId?.value
    ? interItem
    : values?.items?.filter((item: any) => item.product !== '');
  const typeLineId = types?.[categoryId?.value]?.lineId;
  return (
    <Grid container spacing={2} sx={{ paddingX: 0 }}>
      <Grid item xs={12} md={8}>
        <Stack
          divider={<Divider flexItem sx={{ borderStyle: 'dashed' }} />}
          spacing={3}
          sx={{
            height: 500,
            overflow: 'auto',
          }}
        >
          {categoryId?.value ? (
            <Button
              variant="outlined"
              fullWidth
              color="secondary"
              onClick={() => {
                methods.reset();
                setInterItem([]);
              }}
            >
              Change Type
            </Button>
          ) : (
            <RHFCardSelect
              name="category"
              label={translates.category}
              options={Object.values(data?.types as any)}
              control={methods.control}
              wrap
            />
          )}
          {!!categoryId?.value &&
            typeLineId &&
            Object.values(data?.products[typeLineId] as any).map((category: any, index: number) => (
              <RHFCardSelect
                key={`category-${index}`}
                name="product"
                wrap
                label={category.name}
                options={category.products}
                control={methods.control}
              />
            ))}
        </Stack>
      </Grid>
      <Grid item xs={12} md={4} sx={{ px: 1 }}>
        <Stack spacing={3}>
          <Card sx={{ height: 300 }}>
            <CardContent
              sx={{
                overflow: 'scroll',
              }}
            >
              <Stack spacing={2}>
                {itemArrayProducts.map((item: any, index: number) => (
                  <Card key={`${item.product.value}-${item.quantity}-${index}`}>
                    <CardContent>
                      <Stack direction="row" justifyContent="space-between" spacing={2}>
                        <Stack justifyContent="center" alignItems="center">
                          <Typography>{item.product.label}</Typography>
                        </Stack>
                        <Stack justifyContent="center" alignItems="center">
                          <Typography fontWeight="bold">{item.quantity}</Typography>
                          <Typography fontSize={12}>{translates.quantity}</Typography>
                        </Stack>
                      </Stack>
                    </CardContent>
                  </Card>
                ))}
              </Stack>
            </CardContent>
          </Card>

          <Card>
            <CardContent>
              <Stack direction="column" spacing={2}>
                <RHFTextField
                  control={methods.control}
                  name="description"
                  fullWidth
                  label={translates.description}
                />
                <Stack direction="row" justifyContent="space-between" spacing={2}>
                  <RHFQuantity
                    disabled
                    control={methods.control}
                    name="quantity"
                    defaultValue={1}
                  />
                  <Stack direction="row" justifyContent="center">
                    <Button
                      size="small"
                      startIcon={<Iconic icon="el:ok-sign" />}
                      onClick={methods.handleSubmit(handleAdd)}
                      variant="contained"
                      sx={{ flexShrink: 0 }}
                    >
                      {translates.add}
                    </Button>
                  </Stack>
                </Stack>
                <Button
                  size="small"
                  onClick={completeProduct}
                  variant="outlined"
                  color="secondary"
                  sx={{ flexShrink: 0 }}
                >
                  {translates.completeProduct}
                </Button>
              </Stack>
            </CardContent>
          </Card>
        </Stack>
      </Grid>
    </Grid>
  );
};
